import { getIconComponent } from "@storytell/bsky-icons";
import type { Asset, AssetId, Concept, ConceptId, Label, LabelId, View, ViewId } from "@storytell/types";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbAlertTriangle, TbApps, TbFileText, TbLoader2, TbStack, TbX } from "solid-icons/tb";
import { type Component, For, Match, Show, Switch } from "solid-js";
import { Dynamic } from "solid-js/web";
import { twMerge } from "tailwind-merge";
import { LabelTooltip } from "~/components/labels/LabelTooltip";
import { isLightColor } from "~/components/layouts/minimal/isLightColor";
import { lifecycleStateGrouped } from "~/components/utils/lifecycleStateMappers";
import { truncateMiddle } from "~/utils/truncateMiddle";

type UploadingAssetLike = {
  id: string;
  status: string;
  filename: string;
};

interface ScopedItemsListProps {
  views: () => View[];
  labels: () => Label[];
  concepts: () => Concept[];
  assets: () => Asset[];
  uploadingAssets?: () => UploadingAssetLike[];

  allowRemove?: boolean;
  onRemoveView?: (id: ViewId) => void;
  onRemoveLabel?: (id: LabelId) => void;
  onRemoveConcept?: (id: ConceptId) => void;
  onRemoveAsset?: (id: AssetId) => void;

  showClearAll?: boolean;
  onClearAll?: () => void;
}

const truncateFilename = (filename: string, maxLength = 25) => {
  if (filename.length <= maxLength) return filename;
  const extension = filename.split(".").pop();
  const nameWithoutExt = filename.substring(0, filename.lastIndexOf("."));
  const truncatedName = nameWithoutExt.substring(0, maxLength - (extension ? extension.length : 0) - 4);
  return `${truncatedName}...${extension ?? ""}`;
};

const truncateConceptName = (name: string, maxLength = 25) => {
  if (name.length <= maxLength) return name;
  return `${name.substring(0, maxLength)}...`;
};

export const ScopedItemsList: Component<ScopedItemsListProps> = (props) => {
  const allowRemove = () => props.allowRemove !== false;

  const totalItems = () =>
    (props.assets()?.length ?? 0) +
    (props.concepts()?.length ?? 0) +
    (props.labels()?.length ?? 0) +
    (props.views()?.length ?? 0);

  return (
    <>
      <div class="flex items-center gap-2 flex-auto overflow-x-auto scrollbar-hide">
        <For each={props.views()}>
          {(view) => (
            <span class="group/btn shrink-0 flex bg-canvas hover:bg-canvas-hover border border-primary-border rounded-full items-center gap-1 py-1 px-2 select-none">
              <TbStack class="size-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-secondary-text" />
              <span>{truncateConceptName(view.name)}</span>

              <Show when={allowRemove()}>
                <button
                  type="button"
                  title={`Remove ${view.name} from scope`}
                  aria-label={`Remove ${view.name} from scope`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (props.onRemoveView) props.onRemoveView(view.id);
                  }}
                >
                  <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform" />
                </button>
              </Show>
            </span>
          )}
        </For>

        <For each={props.labels()}>
          {(label) => (
            <StTooltip
              placement="top"
              contentProps={{
                class: "z-50",
              }}
              content={<LabelTooltip labelId={label.id} />}
            >
              <StTooltipTrigger
                as="span"
                class="group/btn shrink-0 flex bg-canvas hover:bg-canvas-hover border border-primary-border rounded-full items-center gap-1 py-1 px-2 select-none"
              >
                <span
                  class="rounded-full size-4 grid place-content-center bg-[var(--label-color)]"
                  style={{
                    "--label-color": label.color,
                    "--label-icon": isLightColor(label.color || "") ? "black" : "white",
                  }}
                >
                  <Dynamic
                    component={getIconComponent(label.icon)}
                    class="text-[var(--label-icon)] group-hover/btn:scale-hover group-active/btn:scale-active transition-transform"
                  />
                </span>
                <span>{truncateMiddle(label.name)}</span>

                <Show when={allowRemove()}>
                  <button
                    type="button"
                    title={`Remove ${label.name} from scope`}
                    aria-label={`Remove ${label.name} from scope`}
                    onClick={(e) => {
                      e.preventDefault();
                      if (props.onRemoveLabel) props.onRemoveLabel(label.id);
                    }}
                  >
                    <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform" />
                  </button>
                </Show>
              </StTooltipTrigger>
            </StTooltip>
          )}
        </For>

        <For each={props.concepts()}>
          {(concept) => (
            <span class="group/btn shrink-0 flex bg-concept-bg text-concept-text hover:bg-concept-bg-hover border border-concept-border rounded-full items-center gap-1 py-1 px-2 select-none">
              <TbApps class="size-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-concept-icon" />
              <span>{truncateConceptName(concept.name)}</span>

              <Show when={allowRemove()}>
                <button
                  type="button"
                  title={`Remove ${concept.name} from scope`}
                  aria-label={`Remove ${concept.name} from scope`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (props.onRemoveConcept) props.onRemoveConcept(concept.id);
                  }}
                >
                  <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform" />
                </button>
              </Show>
            </span>
          )}
        </For>

        <For each={props.uploadingAssets ? props.uploadingAssets() : []}>
          {(asset) => (
            <span class="group/btn shrink-0 flex bg-asset-bg hover:bg-asset-bg-hover text-asset-text border border-asset-border rounded-full items-center gap-1 py-1 px-2 select-none">
              <Switch>
                <Match when={asset.status === "uploading"}>
                  <TbLoader2 class="w-3 h-3 animate-spin group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-asset-icon" />
                </Match>
                <Match when={asset.status === "error"}>
                  <TbAlertTriangle class="w-3 h-3 text-red-700 dark:text-red-400 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform" />
                </Match>
              </Switch>
              <span class="text-xs">{truncateFilename(asset.filename)}</span>

              <Show when={allowRemove()}>
                <button
                  type="button"
                  title={`Remove ${asset.filename} from scope`}
                  aria-label={`Remove ${asset.filename} from scope`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (props.onRemoveAsset) props.onRemoveAsset(asset.id as AssetId);
                  }}
                >
                  <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform" />
                </button>
              </Show>
            </span>
          )}
        </For>

        <For each={props.assets()}>
          {(asset) => (
            <span class="group/btn shrink-0 flex bg-asset-bg hover:bg-asset-bg-hover text-asset-text border border-asset-border rounded-full items-center gap-1 py-1 px-2 select-none">
              <Switch>
                <Match when={["uploading", "processing"].includes(lifecycleStateGrouped(asset.lifecycleState))}>
                  <TbLoader2 class="w-3 h-3 animate-spin group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-asset-icon" />
                </Match>
                <Match when={lifecycleStateGrouped(asset.lifecycleState) === "error"}>
                  <TbAlertTriangle class="w-3 h-3 text-red-700 dark:text-red-400 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform" />
                </Match>
                <Match when={lifecycleStateGrouped(asset.lifecycleState) === "ready"}>
                  <TbFileText class="w-3 h-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-asset-icon" />
                </Match>
              </Switch>
              <span>
                {truncateFilename(
                  (asset as unknown as { fileName?: string; filename?: string }).fileName ||
                    (asset as unknown as { filename?: string }).filename ||
                    "",
                )}
              </span>

              <Show when={allowRemove()}>
                <button
                  type="button"
                  title={`Remove ${((asset as unknown as { fileName?: string; filename?: string }).fileName || (asset as unknown as { filename?: string }).filename || "asset").toString()} from scope`}
                  aria-label={`Remove ${((asset as unknown as { fileName?: string; filename?: string }).fileName || (asset as unknown as { filename?: string }).filename || "asset").toString()} from scope`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (props.onRemoveAsset) props.onRemoveAsset(asset.id as AssetId);
                  }}
                >
                  <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform" />
                </button>
              </Show>
            </span>
          )}
        </For>
      </div>

      <Show when={props.showClearAll && totalItems() > 1 && allowRemove()}>
        <button
          type="button"
          onClick={props.onClearAll}
          class={twMerge(
            "text-xs shrink-0 text-muted-text hover:text-secondary-text transition-colors underline cursor-pointer select-none",
          )}
          title="Clear all assets from scope"
        >
          Clear all
        </button>
      </Show>
    </>
  );
};
